import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2, Flame, Droplets, Utensils } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getMenuCategories, createMenuItem, updateMenuItem, deleteMenuItem, uploadImage } from '../api/api';
import api from '../api/axios';

const EMPTY_FORM = {
  name: '', price: '', description: '', image: '',
  sauce_autorisee: null, accompagnement_autorise: null, cuisson_autorisee: null,
  sauces_disponibles: [], accompagnements_disponibles: [],
};

const MenuManagement = () => {
  const navigate = useNavigate();
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [optionsConfig, setOptionsConfig] = useState({ sauces: [], accompagnements: [] });

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchMenuData();
    api.get('/menu/sauces-config').then(({ data }) => setOptionsConfig(data)).catch(() => {});
  }, [navigate]);

  const fetchMenuData = async () => {
    try {
      const data = await getMenuCategories();
      setMenuData(data);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger le menu', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleEdit = (categoryId, item) => {
    setEditingItem({ categoryId, itemId: item.id });
    setSelectedCategory(categoryId);
    setFormData({
      ...EMPTY_FORM, name: item.name, price: item.price, description: item.description || '',
      image: item.image || '', sauce_autorisee: item.sauce_autorisee ?? null,
      accompagnement_autorise: item.accompagnement_autorise ?? null,
      cuisson_autorisee: item.cuisson_autorisee ?? null,
      sauces_disponibles: item.sauces_disponibles || [],
      accompagnements_disponibles: item.accompagnements_disponibles || [],
    });
    setIsDialogOpen(true);
  };

  const handleAdd = (categoryId) => {
    setEditingItem(null);
    setSelectedCategory(categoryId);
    setFormData(EMPTY_FORM);
    setIsDialogOpen(true);
  };

  const handleDelete = async (categoryId, itemId) => {
    if (window.confirm('Supprimer cet item ?')) {
      try {
        await deleteMenuItem(itemId);
        toast({ title: 'Item supprime' });
        fetchMenuData();
      } catch (error) {
        toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateMenuItem(editingItem.itemId, formData);
        toast({ title: 'Item modifie' });
      } else {
        await createMenuItem({ category_id: selectedCategory, ...formData });
        toast({ title: 'Item ajoute' });
      }
      setIsDialogOpen(false);
      setFormData(EMPTY_FORM);
      fetchMenuData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Erreur', description: 'Taille max: 5MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const data = await uploadImage(file);
      setFormData({ ...formData, image: data.imageUrl });
      toast({ title: 'Image chargee' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger l\'image', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const toggleDishOption = (field, name) => {
    setFormData(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(name)
          ? current.filter(value => value !== name)
          : [...current, name],
      };
    });
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" data-testid="menu-management-title">Gestion du Menu</h1>
          <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
            <ArrowLeft size={14} className="mr-1" /> Dashboard
          </Button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {menuData.map((category) => (
          <Card key={category.id} className="border shadow-sm">
            <CardHeader className="pb-2 pt-3 px-4 flex flex-row justify-between items-center">
              <CardTitle className="text-lg text-red-600">{category.name}</CardTitle>
              <Button onClick={() => handleAdd(category.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7">
                <Plus size={12} className="mr-1" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="space-y-1">
                {category.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {item.image && <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded shrink-0" />}
                      <div className="min-w-0">
                        <span className="font-medium block truncate">{item.name}</span>
                        {item.description && <span className="text-xs text-gray-500 block truncate">{item.description}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[#8b4513] font-bold">{item.price}&euro;</span>
                      <Button onClick={() => handleEdit(category.id, item)} variant="ghost" size="sm" className="h-6 w-6 p-0"><Edit size={12} /></Button>
                      <Button onClick={() => handleDelete(category.id, item.id)} variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500"><Trash2 size={12} /></Button>
                    </div>
                  </div>
                ))}
                {category.items.length === 0 && <p className="text-xs text-gray-400 py-2">Aucun item dans cette categorie</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier l\'item' : 'Nouvel item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-xs">Nom *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Entrecote" required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="price" className="text-xs">Prix *</Label>
              <Input id="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="Ex: 25.50" required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="description" className="text-xs">Description</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Avec sauce au poivre" rows={2} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Image</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="text-xs" />
                {uploading && <Loader2 className="animate-spin text-gray-400" size={18} />}
              </div>
              {formData.image && <img src={formData.image} alt="Preview" className="w-20 h-20 object-cover rounded mt-1" />}
              <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="Ou URL d'image" className="text-xs mt-1" />
            </div>
            <div className="border rounded-lg p-3 bg-amber-50 space-y-2.5">
              <p className="text-xs font-bold text-amber-800">Options autorisees (vide = defaut categorie)</p>
              {[
                { key: 'sauce_autorisee', label: 'Sauce', icon: Droplets, color: 'text-amber-600' },
                { key: 'accompagnement_autorise', label: 'Accompagnement', icon: Utensils, color: 'text-green-600' },
                { key: 'cuisson_autorisee', label: 'Cuisson', icon: Flame, color: 'text-red-600' },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center justify-between" data-testid={`toggle-${key}`}>
                  <div className="flex items-center gap-2">
                    <Icon size={14} className={color} />
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => setFormData({ ...formData, [key]: formData[key] === null ? true : formData[key] === true ? false : null })}
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold border transition-colors ${
                        formData[key] === null ? 'bg-gray-100 text-gray-500 border-gray-200' :
                        formData[key] === true ? 'bg-green-100 text-green-700 border-green-300' :
                        'bg-red-100 text-red-700 border-red-300'
                      }`}>
                      {formData[key] === null ? 'Defaut' : formData[key] ? 'OUI' : 'NON'}
                    </button>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-amber-200">
                <p className="text-xs font-bold text-amber-800 mb-1">Sauces disponibles pour ce plat</p>
                <p className="text-[10px] text-gray-500 mb-2">Aucune sélection = toutes les sauces globales.</p>
                <div className="flex flex-wrap gap-1">
                  {(optionsConfig.sauces || []).map(name => (
                    <button key={name} type="button" onClick={() => toggleDishOption('sauces_disponibles', name)}
                      className={`px-2 py-1 rounded-full text-[10px] border ${formData.sauces_disponibles.includes(name) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t border-amber-200">
                <p className="text-xs font-bold text-green-800 mb-1">Accompagnements disponibles pour ce plat</p>
                <p className="text-[10px] text-gray-500 mb-2">Aucune sélection = tous les accompagnements globaux.</p>
                <div className="flex flex-wrap gap-1">
                  {(optionsConfig.accompagnements || []).map(option => {
                    const name = typeof option === 'object' ? option.name : option;
                    return (
                      <button key={name} type="button" onClick={() => toggleDishOption('accompagnements_disponibles', name)}
                        className={`px-2 py-1 rounded-full text-[10px] border ${formData.accompagnements_disponibles.includes(name) ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1 bg-[#2c1810] hover:bg-[#4a2c1a]">
                <Save size={14} className="mr-1" /> {editingItem ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
